import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import Box from 'apollo-react/components/Box';
import Grid from 'apollo-react/components/Grid';
import TextField from 'apollo-react/components/TextField';
import Select from 'apollo-react/components/Select';
import MenuItem from 'apollo-react/components/MenuItem';
import DatePicker from 'apollo-react/components/DatePicker';
import PhoneNumberInput from 'apollo-react/components/PhoneNumberInput';
import Tooltip from 'apollo-react/components/Tooltip';
import Typography from 'apollo-react/components/Typography';
import Button from 'apollo-react/components/Button';
import CircularProgress from 'apollo-react/components/CircularProgress';
import { toast } from 'react-toastify';
import IdDocumentUploadSection from '../components/IdDocumentUploadSection';
import {
  nationalityOptions,
  genderOptions,
  validationConfigDefaults,
  addressCountryOptions,
  getAddressStateOptions,
  getAddressCityOptions,
  countryCodeDialOptions,
  hcpProfileFormFieldVariant,
  hcpProfileDatePickerTextFieldProps,
  hcpProfileTextFieldProps,
  normalizeAddressCountryValue,
  normalizeDialCountryCode,
  mergeOptionIfMissing,
  labelForAddressCountry,
  labelForAddressState,
  labelForAddressCity,
} from '../../../components/HcpSignup/hcpSignupDefaults';
import {
  buildGeneralInfoSchema,
  buildMinimalAccountProfileSchema,
} from '../../../components/HcpSignup/generalInfoSchema';
import {
  HCP_SIGNUP_FIELD_HINTS,
  HcpSignupLabelWithHint,
} from '../../../components/HcpSignup/HcpSignupFieldLabel';
import { putHcpProfileGeneralInfo } from '../hcpProfileApi';
import {
  PROFILE_VERIFICATION_LOCK_MESSAGE,
  PROFILE_VERIFICATION_LOCKED_PATHS,
} from '../hcpProfileFieldLockConfig';
import profileCardStyles from '../../customerProfile/profileUi/Profile.module.css';
import buttonStyles from '../../../components/common/Button.module.css';
import ButtonCommon from '../../../components/common/Button';
import { formatPhoneForStorage } from '../../../utils/phoneUtils';
import {
  datePickerValueToIsoDate,
  isoDateToDatePickerValue,
} from '../apolloDateUtils';
import { yupValidationErrorToFieldMap } from '../utils/yupValidationErrors';

function normalizeProfileGeneralApiErrors(errs) {
  if (!errs || typeof errs !== 'object') return {};
  const out = { ...errs };
  if (out.idImage && !out.idImageFile) {
    out.idImageFile = out.idImage;
  }
  return out;
}

/** `defaultCountry` for Apollo {@link PhoneNumberInput} (react-phone-input-2 ISO2). */
const PROFILE_DIAL_TO_DEFAULT_COUNTRY = Object.freeze({
  '+20': 'eg',
  '+966': 'sa',
  '+971': 'ae',
});

function profilePhoneForPhoneInput(countryCode, phoneNumberDigits) {
  const ccRaw = String(countryCode ?? '').trim();
  const national = String(phoneNumberDigits ?? '').replace(/\D/g, '');
  if (!ccRaw && !national) return '';
  const dial = ccRaw.startsWith('+')
    ? ccRaw.replace(/\++/g, '+')
    : ccRaw
      ? `+${ccRaw.replace(/\D/g, '')}`
      : '';
  if (!national) return dial || '';
  if (!dial) return `+${national}`;
  return `${dial}${national}`;
}

function profilePhoneSplitFromIntl(intlVal) {
  const stored = formatPhoneForStorage(intlVal || '') || '';
  if (!stored) return { countryCode: '', phoneNumber: '' };
  const sorted = [...countryCodeDialOptions].sort(
    (a, b) =>
      String(b.value).replace(/\D/g, '').length -
      String(a.value).replace(/\D/g, '').length
  );
  for (const o of sorted) {
    const codeDigits = o.value.replace(/\D/g, '');
    if (codeDigits && stored.startsWith(codeDigits)) {
      return {
        countryCode: o.value,
        phoneNumber: stored.slice(codeDigits.length),
      };
    }
  }
  return { countryCode: '', phoneNumber: stored };
}

/** @param {string} text @param {keyof typeof HCP_SIGNUP_FIELD_HINTS} [hintKey] */
function profileSignupLabel(text, hintKey) {
  return (
    <HcpSignupLabelWithHint
      text={text}
      hint={hintKey ? HCP_SIGNUP_FIELD_HINTS[hintKey] : undefined}
    />
  );
}

function formFromSegment(seg) {
  const s = seg || {};
  const dob = s.dateOfBirth ? String(s.dateOfBirth).slice(0, 10) : '';
  const catName =
    s.categoryName != null && String(s.categoryName).trim() !== ''
      ? String(s.categoryName).trim()
      : s.category_name != null && String(s.category_name).trim() !== ''
        ? String(s.category_name).trim()
        : '';
  return {
    firstName: s.firstName || '',
    lastName: s.lastName || '',
    email: s.email || '',
    categoryName: catName,
    dateOfBirth: dob,
    idNumber: s.idNumber || '',
    nationality: s.nationality || '',
    countryCode: normalizeDialCountryCode(s.countryCode || ''),
    phoneNumber: s.phoneNumber || '',
    gender: s.gender || '',
    addressCountry: normalizeAddressCountryValue(s.addressCountry || ''),
    addressState: s.addressState || '',
    addressCity: s.addressCity || '',
    addressStreet: s.addressStreet || '',
    addressZipCode: s.addressZipCode || '',
  };
}

/** @typedef {typeof validationConfigDefaults} ValidationCfg */

/** @typedef {"full" | "personal" | "address"} GeneralInfoVariant */

function LockedHint({ path, lockHintFn }) {
  return (
    <Tooltip title={lockHintFn(path)} placement="top" enterDelay={200}>
      <span
        tabIndex={0}
        role="note"
        style={{ cursor: 'help', textDecoration: 'underline', marginLeft: 8 }}
      >
        <Typography variant="bodySmall" color="text.secondary" component="span">
          Locked
        </Typography>
      </span>
    </Tooltip>
  );
}

export default function GeneralInformationTab({
  segment,
  lockedPaths,
  lockMessages = {},
  verificationLockedPaths = [...PROFILE_VERIFICATION_LOCKED_PATHS],
  validationConfig,
  attachmentRevisions = [],
  onSaved,
  variant = 'full',
  showTitle = true,
  cardFieldLayout = false,
  /** When true (e.g. request flow): skip view mode — fields are editable; only Save is shown. */
  embedInlineEdit = false,
  /** When true with `embedInlineEdit`: hide Save and persist changes on debounce (request flow). */
  embedInlineAutosave = false,
  /** Snapshot / reviewer: no Edit/Save; applicant fields are display-only (attached copy). */
  readOnly = false,
  /** License tracking: only first name, last name, email, and category. */
  accountFieldsOnly = false,
  /** When false, only the current file is shown (hide prior-version / audit lists). */
  showAttachmentPriorVersions = true,
}) {
  const segmentData = useMemo(() => segment || {}, [segment]);
  const locked = useMemo(() => new Set(lockedPaths || []), [lockedPaths]);
  const verificationSet = useMemo(
    () => new Set(verificationLockedPaths || []),
    [verificationLockedPaths]
  );
  /** @type {ValidationCfg} */
  const mergedConfig = useMemo(
    () => ({ ...validationConfigDefaults, ...(validationConfig || {}) }),
    [validationConfig]
  );
  const schema = useMemo(
    () =>
      accountFieldsOnly
        ? buildMinimalAccountProfileSchema(mergedConfig)
        : buildGeneralInfoSchema(mergedConfig, {
            requirePortalPassword: false,
            requireCategory: false,
          }),
    [mergedConfig, accountFieldsOnly]
  );

  const [fieldErrors, setFieldErrors] = useState({});

  const [mode, setMode] = useState(embedInlineEdit ? 'edit' : 'view');
  const [saving, setSaving] = useState(false);
  const [baseline, setBaseline] = useState(null);
  const [form, setForm] = useState(() => formFromSegment(segmentData));
  const [idFile, setIdFile] = useState(null);

  const serializeGeneralFormState = useCallback((f, hasIdFile) => {
    return JSON.stringify({
      ...f,
      phoneNumber: String(f.phoneNumber || '').replace(/\D/g, ''),
      _hasNewIdFile: Boolean(hasIdFile),
    });
  }, []);

  const lastAutosaveSerializedRef = useRef('');
  const autosaveTimerRef = useRef(null);

  useEffect(() => {
    if (readOnly) {
      setMode('view');
      setBaseline(null);
      setIdFile(null);
    }
  }, [readOnly]);

  useEffect(() => {
    if (embedInlineEdit) {
      const next = formFromSegment(segmentData);
      setForm(next);
      if (embedInlineAutosave) {
        lastAutosaveSerializedRef.current = serializeGeneralFormState(
          next,
          null
        );
      }
      return;
    }
    if (mode !== 'view') return;
    setForm(formFromSegment(segmentData));
  }, [
    segmentData,
    mode,
    embedInlineEdit,
    embedInlineAutosave,
    serializeGeneralFormState,
  ]);

  const change = useCallback((k) => {
    return (e) => {
      setFieldErrors((prev) => {
        if (!prev[k]) return prev;
        const next = { ...prev };
        delete next[k];
        return next;
      });
      setForm((p) => ({ ...p, [k]: e.target.value }));
    };
  }, []);

  const addressStateBaseOptions = useMemo(
    () => getAddressStateOptions(form.addressCountry),
    [form.addressCountry]
  );
  const addressStateSelectOptions = useMemo(
    () => mergeOptionIfMissing(addressStateBaseOptions, form.addressState),
    [addressStateBaseOptions, form.addressState]
  );
  const addressCityBaseOptions = useMemo(
    () => getAddressCityOptions(form.addressCountry, form.addressState),
    [form.addressCountry, form.addressState]
  );
  const addressCitySelectOptions = useMemo(
    () => mergeOptionIfMissing(addressCityBaseOptions, form.addressCity),
    [addressCityBaseOptions, form.addressCity]
  );
  const addressCountrySelectOptions = useMemo(
    () => mergeOptionIfMissing(addressCountryOptions, form.addressCountry),
    [form.addressCountry]
  );

  const viewSnapshot = useMemo(
    () => formFromSegment(segmentData),
    [segmentData]
  );
  const viewAddressCountrySelectOptions = useMemo(
    () =>
      mergeOptionIfMissing(addressCountryOptions, viewSnapshot.addressCountry),
    [viewSnapshot.addressCountry]
  );
  const viewAddressStateSelectOptions = useMemo(
    () =>
      mergeOptionIfMissing(
        getAddressStateOptions(viewSnapshot.addressCountry),
        viewSnapshot.addressState
      ),
    [viewSnapshot.addressCountry, viewSnapshot.addressState]
  );
  const viewAddressCitySelectOptions = useMemo(
    () =>
      mergeOptionIfMissing(
        getAddressCityOptions(
          viewSnapshot.addressCountry,
          viewSnapshot.addressState
        ),
        viewSnapshot.addressCity
      ),
    [
      viewSnapshot.addressCountry,
      viewSnapshot.addressState,
      viewSnapshot.addressCity,
    ]
  );

  const startEdit = () => {
    if (readOnly) return;
    setFieldErrors({});
    setBaseline(JSON.stringify(formFromSegment(segmentData)));
    setForm(formFromSegment(segmentData));
    setIdFile(null);
    setMode('edit');
  };

  const cancelEdit = () => {
    setFieldErrors({});
    if (baseline != null) {
      try {
        setForm(JSON.parse(baseline));
      } catch {
        setForm(formFromSegment(segmentData));
      }
    } else {
      setForm(formFromSegment(segmentData));
    }
    setBaseline(null);
    setIdFile(null);
    setMode('view');
  };

  const lockHint = (path) => {
    const fromApi = lockMessages[path];
    if (fromApi) return fromApi;
    if (verificationSet.has(path)) return PROFILE_VERIFICATION_LOCK_MESSAGE;
    return 'This field cannot be edited.';
  };

  const handleSave = useCallback(
    async (opts = {}) => {
      if (readOnly) return;
      const silentSuccess = opts.silentSuccess === true;
      const tryValues = {
        ...form,
        phoneNumber: String(form.phoneNumber || '').replace(/\D/g, ''),
        idImageFile: variant === 'address' ? null : idFile || null,
        existingIdImageId: segmentData.idImage?.id || null,
      };
      try {
        await schema.validate(tryValues, { abortEarly: false });
      } catch (yupErr) {
        const map = yupValidationErrorToFieldMap(yupErr);
        setFieldErrors(map);
        if (!silentSuccess) {
          const summary =
            (Object.values(map).length && Object.values(map)[0]) ||
            yupErr.message ||
            'Please fix the highlighted fields.';
          toast.error(summary);
        }
        return;
      }
      setSaving(true);
      try {
        await putHcpProfileGeneralInfo(
          {
            ...form,
            phoneNumber: String(form.phoneNumber || '').replace(/\D/g, ''),
          },
          variant === 'address' ? null : idFile
        );
        setFieldErrors({});
        if (!silentSuccess) {
          toast.success(
            variant === 'address'
              ? 'Address information saved'
              : variant === 'personal'
                ? 'Personal information saved'
                : 'General information saved'
          );
        }
        setBaseline(null);
        if (!embedInlineEdit) {
          setMode('view');
        }
        setIdFile(null);
        if (embedInlineAutosave) {
          lastAutosaveSerializedRef.current = serializeGeneralFormState(
            form,
            null
          );
        }
        await onSaved?.();
      } catch (e) {
        const payload = e.response?.data;
        const errs = payload?.errors;
        if (errs && typeof errs === 'object' && !Array.isArray(errs)) {
          setFieldErrors(normalizeProfileGeneralApiErrors(errs));
          const msg =
            payload?.message ||
            Object.values(errs).find((x) => typeof x === 'string') ||
            'Validation failed';
          toast.error(typeof msg === 'string' ? msg : 'Validation failed');
        } else {
          setFieldErrors({});
          const msg = payload?.message || e.message || 'Save failed';
          toast.error(typeof msg === 'string' ? msg : 'Save failed');
        }
      } finally {
        setSaving(false);
      }
    },
    [
      form,
      idFile,
      variant,
      segmentData,
      schema,
      embedInlineEdit,
      embedInlineAutosave,
      serializeGeneralFormState,
      onSaved,
      readOnly,
    ]
  );

  useEffect(() => {
    if (readOnly) return;
    if (!embedInlineEdit || !embedInlineAutosave) {
      return;
    }
    if (saving) {
      return;
    }
    const snap = serializeGeneralFormState(form, idFile);
    if (snap === lastAutosaveSerializedRef.current) {
      return;
    }
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null;
      handleSave({ silentSuccess: true });
    }, 1300);
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [
    readOnly,
    form,
    idFile,
    embedInlineEdit,
    embedInlineAutosave,
    saving,
    serializeGeneralFormState,
    handleSave,
  ]);

  /**
   * @param {object} opts
   * @param {string} opts.label
   * @param {string} opts.path
   * @param {boolean} opts.editable
   * @param {string} [opts.type]
   * @param {React.ReactNode} [opts.control]
   * @param {6|12} [opts.gridSm]
   * @param {(v: unknown) => string} [opts.formatDisplay]
   * @param {keyof typeof HCP_SIGNUP_FIELD_HINTS} [opts.hintKey]
   */
  const field = ({
    label,
    path,
    editable: modeEditable,
    type = 'text',
    control,
    gridSm = 12,
    formatDisplay,
    hintKey,
  }) => {
    const isLocked = locked.has(path);
    const rawValue = modeEditable && !isLocked ? form[path] : segmentData[path];
    let displayValue;
    if (rawValue === undefined || rawValue === null || rawValue === '') {
      displayValue = '—';
    } else if (formatDisplay) {
      displayValue = formatDisplay(rawValue);
    } else {
      displayValue = String(rawValue);
    }
    const valueForInput =
      rawValue === undefined || rawValue === null ? '' : String(rawValue);

    if (cardFieldLayout && !modeEditable) {
      return (
        <div className={profileCardStyles.row} key={path}>
          <span className={profileCardStyles.label}>
            {hintKey ? profileSignupLabel(label, hintKey) : label}:
          </span>
          <span className={profileCardStyles.value}>
            <Typography
              variant="body"
              component="span"
              sx={{ wordBreak: 'break-word' }}
            >
              {displayValue}
            </Typography>
            {isLocked ? <LockedHint path={path} lockHintFn={lockHint} /> : null}
          </span>
        </div>
      );
    }

    if (!modeEditable) {
      return (
        <div className="mb-3" key={path}>
          <Typography
            variant="bodySmall"
            color="text.secondary"
            sx={{ mb: 0.5 }}
            component="div"
          >
            {hintKey ? profileSignupLabel(label, hintKey) : label}
          </Typography>
          <Typography variant="body" sx={{ wordBreak: 'break-word' }}>
            {displayValue}
          </Typography>
        </div>
      );
    }

    let inner;
    const textLabel = profileSignupLabel(label, hintKey);
    const fieldErr =
      modeEditable && !isLocked && fieldErrors[path] ? fieldErrors[path] : '';
    if (!isLocked) {
      inner =
        control ||
        (type === 'date' ? null : (
          <TextField
            {...hcpProfileTextFieldProps}
            label={textLabel}
            type={type === 'email' ? 'email' : 'text'}
            value={valueForInput}
            onChange={change(path)}
            name={path}
            error={Boolean(fieldErr)}
            helperText={fieldErr || ''}
          />
        ));
    } else {
      inner = (
        <TextField
          {...hcpProfileTextFieldProps}
          label={textLabel}
          type={type === 'email' ? 'email' : type === 'date' ? 'text' : 'text'}
          value={valueForInput}
          disabled
          helperText={lockHint(path)}
          name={path}
        />
      );
    }

    return (
      <Grid item xs={12} sm={gridSm} key={path}>
        {inner}
      </Grid>
    );
  };

  const editable = mode === 'edit';

  const sectionTitle =
    variant === 'personal'
      ? 'Personal Information'
      : variant === 'address'
        ? 'Address Information'
        : 'General information';

  const showPersonal = variant === 'full' || variant === 'personal';
  const showAddress =
    !accountFieldsOnly && (variant === 'full' || variant === 'address');
  const showExtendedPersonal = !accountFieldsOnly;
  const showIdUpload =
    showExtendedPersonal &&
    (variant === 'personal' || (variant === 'full' && showPersonal));

  const idDocumentUploadHelperText = showAttachmentPriorVersions
    ? 'Upload a new PDF or image (max 10 MB) to replace the ID document. Previous files stay in your history for audit.'
    : 'Upload a new PDF or image (max 10 MB) to replace the ID document.';

  const dualCol = showAddress && variant === 'full';

  const saveButtonInner = saving ? (
    <>
      <CircularProgress size={18} style={{ marginRight: 8 }} />
      Saving…
    </>
  ) : (
    'Save'
  );

  const headerBar = !cardFieldLayout ? (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
      gap={2}
      flexWrap="wrap"
    >
      {showTitle ? (
        <Typography variant="h5" component="h2" sx={{ mb: 0 }}>
          {sectionTitle}
        </Typography>
      ) : (
        <Box flex="1 1 auto" />
      )}
      {mode === 'view' && !readOnly ? (
        <Button
          variant="secondary"
          size="small"
          className={buttonStyles.button}
          onClick={startEdit}
        >
          Edit
        </Button>
      ) : (
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <Button
            variant="secondary"
            status="danger"
            size="small"
            className={buttonStyles.button}
            onClick={cancelEdit}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="small"
            className={buttonStyles.button}
            disabled={saving}
            onClick={() => handleSave()}
          >
            {saveButtonInner}
          </Button>
        </Box>
      )}
    </Box>
  ) : showTitle ? (
    <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
      {sectionTitle}
    </Typography>
  ) : null;

  const cardActions = readOnly ? null : embedInlineEdit &&
    embedInlineAutosave ? null : embedInlineEdit && cardFieldLayout ? (
    <div className={profileCardStyles.actionsRight}>
      <ButtonCommon
        color="primary"
        className={profileCardStyles.btnSized}
        disabled={saving}
        onClick={() => handleSave()}
        text={saving ? 'Saving…' : 'Save'}
      />
    </div>
  ) : cardFieldLayout && mode === 'view' ? (
    <div className={profileCardStyles.actionsRight}>
      <ButtonCommon
        color="primary"
        className={profileCardStyles.btnSized}
        onClick={startEdit}
        text="Edit"
      />
    </div>
  ) : cardFieldLayout && mode === 'edit' ? (
    <div className={profileCardStyles.actionsRight}>
      <ButtonCommon
        color="secondary"
        status="danger"
        className={`${profileCardStyles.btnSized} ${profileCardStyles.btnMarginRight}`}
        onClick={cancelEdit}
        disabled={saving}
        text="Cancel"
      />
      <ButtonCommon
        color="primary"
        className={profileCardStyles.btnSized}
        disabled={saving}
        onClick={() => handleSave()}
        text={saving ? 'Saving…' : 'Save'}
      />
    </div>
  ) : null;

  const personalFieldsEdit = (
    <>
      {field({
        label: 'First name',
        path: 'firstName',
        hintKey: 'firstName',
        editable: true,
        gridSm: 6,
      })}
      {field({
        label: 'Last name',
        path: 'lastName',
        hintKey: 'lastName',
        editable: true,
        gridSm: 6,
      })}
      {field({
        label: 'Email',
        path: 'email',
        editable: true,
        type: 'email',
        control: !locked.has('email') ? (
          <TextField
            {...hcpProfileTextFieldProps}
            label={profileSignupLabel('Email', 'email')}
            type="email"
            value={form.email}
            onChange={change('email')}
            name="email"
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email || ''}
          />
        ) : (
          <TextField
            {...hcpProfileTextFieldProps}
            label={profileSignupLabel('Email', 'email')}
            type="email"
            value={form.email}
            disabled
            helperText={lockHint('email')}
            name="email"
          />
        ),
      })}
      {field({
        label: 'Category',
        path: 'categoryName',
        hintKey: 'category',
        editable: true,
        control: (
          <TextField
            {...hcpProfileTextFieldProps}
            label={profileSignupLabel('Category', 'category')}
            value={
              segmentData.categoryName != null &&
              String(segmentData.categoryName).trim() !== ''
                ? String(segmentData.categoryName).trim()
                : segmentData.category_name != null &&
                    String(segmentData.category_name).trim() !== ''
                  ? String(segmentData.category_name).trim()
                  : '—'
            }
            disabled
            name="categoryName"
          />
        ),
      })}
      {showExtendedPersonal ? (
        <>
          {field({
            label: 'Date of birth',
            path: 'dateOfBirth',
            editable: true,
            type: 'date',
            control: !locked.has('dateOfBirth') ? (
              <DatePicker
                label={profileSignupLabel('Date of birth', 'dateOfBirth')}
                fullWidth
                value={isoDateToDatePickerValue(form.dateOfBirth)}
                onChange={(v) => {
                  setFieldErrors((prev) => {
                    if (!prev.dateOfBirth) return prev;
                    const next = { ...prev };
                    delete next.dateOfBirth;
                    return next;
                  });
                  setForm((p) => ({
                    ...p,
                    dateOfBirth: datePickerValueToIsoDate(v),
                  }));
                }}
                name="dateOfBirth"
                TextFieldProps={{
                  ...hcpProfileDatePickerTextFieldProps,
                  error: Boolean(fieldErrors.dateOfBirth),
                  helperText: fieldErrors.dateOfBirth || '',
                }}
              />
            ) : (
              <DatePicker
                label={profileSignupLabel('Date of birth', 'dateOfBirth')}
                fullWidth
                value={isoDateToDatePickerValue(form.dateOfBirth)}
                onChange={() => {}}
                disabled
                helperText={lockHint('dateOfBirth')}
                name="dateOfBirth"
                TextFieldProps={hcpProfileDatePickerTextFieldProps}
              />
            ),
          })}
          {field({
            label: 'Nationality',
            path: 'nationality',
            editable: true,
            control: !locked.has('nationality') ? (
              <Select
                label="Nationality"
                fullWidth
                variant={hcpProfileFormFieldVariant}
                value={form.nationality}
                onChange={change('nationality')}
                name="nationality"
                error={Boolean(fieldErrors.nationality)}
                helperText={fieldErrors.nationality || ''}
              >
                <MenuItem value="" disabled>
                  <em>Select…</em>
                </MenuItem>
                {nationalityOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Select
                label="Nationality"
                fullWidth
                variant={hcpProfileFormFieldVariant}
                value={form.nationality}
                disabled
                helperText={lockHint('nationality')}
                name="nationality"
              >
                <MenuItem value="" disabled>
                  <em>Select…</em>
                </MenuItem>
                {nationalityOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            ),
          })}
          {field({
            label: 'ID number',
            path: 'idNumber',
            editable: true,
            control: !locked.has('idNumber') ? (
              <TextField
                {...hcpProfileTextFieldProps}
                label={profileSignupLabel('ID number', 'idNumber')}
                value={form.idNumber}
                onChange={change('idNumber')}
                name="idNumber"
                error={Boolean(fieldErrors.idNumber)}
                helperText={fieldErrors.idNumber || ''}
              />
            ) : (
              <TextField
                {...hcpProfileTextFieldProps}
                label={profileSignupLabel('ID number', 'idNumber')}
                value={form.idNumber}
                disabled
                helperText={lockHint('idNumber')}
                name="idNumber"
              />
            ),
          })}
          {!locked.has('countryCode') && !locked.has('phoneNumber') ? (
            <Grid item xs={12} key="phoneCombined">
              <PhoneNumberInput
                label={profileSignupLabel('Phone number', 'phoneNumber')}
                defaultCountry={
                  PROFILE_DIAL_TO_DEFAULT_COUNTRY[form.countryCode] || 'eg'
                }
                value={profilePhoneForPhoneInput(
                  form.countryCode,
                  form.phoneNumber
                )}
                onChange={(val) => {
                  setFieldErrors((prev) => {
                    if (!prev.countryCode && !prev.phoneNumber) return prev;
                    const next = { ...prev };
                    delete next.countryCode;
                    delete next.phoneNumber;
                    return next;
                  });
                  const next = profilePhoneSplitFromIntl(val);
                  setForm((p) => ({
                    ...p,
                    countryCode: next.countryCode
                      ? normalizeDialCountryCode(next.countryCode)
                      : p.countryCode,
                    phoneNumber: String(next.phoneNumber || '').replace(
                      /\D/g,
                      ''
                    ),
                  }));
                }}
                fullWidth
                name="phoneCombined"
                helperText={
                  fieldErrors.countryCode || fieldErrors.phoneNumber || ''
                }
                error={Boolean(
                  fieldErrors.countryCode || fieldErrors.phoneNumber
                )}
              />
            </Grid>
          ) : (
            <>
              {field({
                label: 'Country code',
                path: 'countryCode',
                editable: true,
                control: !locked.has('countryCode') ? (
                  <Select
                    label={profileSignupLabel('Country code', 'countryCode')}
                    fullWidth
                    variant={hcpProfileFormFieldVariant}
                    value={form.countryCode}
                    onChange={change('countryCode')}
                    name="countryCode"
                    error={Boolean(fieldErrors.countryCode)}
                    helperText={fieldErrors.countryCode || ''}
                  >
                    <MenuItem value="" disabled>
                      <em>Select code</em>
                    </MenuItem>
                    {countryCodeDialOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Select
                    label={profileSignupLabel('Country code', 'countryCode')}
                    fullWidth
                    variant={hcpProfileFormFieldVariant}
                    value={form.countryCode}
                    disabled
                    helperText={lockHint('countryCode')}
                    name="countryCode"
                  >
                    <MenuItem value="" disabled>
                      <em>Select code</em>
                    </MenuItem>
                    {countryCodeDialOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                ),
              })}
              {field({
                label: 'Phone number',
                path: 'phoneNumber',
                editable: true,
                control: !locked.has('phoneNumber') ? (
                  <TextField
                    {...hcpProfileTextFieldProps}
                    label={profileSignupLabel('Phone number', 'phoneNumber')}
                    type="tel"
                    value={form.phoneNumber}
                    onChange={(e) => {
                      setFieldErrors((prev) => {
                        if (!prev.phoneNumber) return prev;
                        const next = { ...prev };
                        delete next.phoneNumber;
                        return next;
                      });
                      setForm((p) => ({
                        ...p,
                        phoneNumber: String(e.target.value).replace(/\D/g, ''),
                      }));
                    }}
                    name="phoneNumber"
                    error={Boolean(fieldErrors.phoneNumber)}
                    helperText={fieldErrors.phoneNumber || ''}
                  />
                ) : (
                  <TextField
                    {...hcpProfileTextFieldProps}
                    label={profileSignupLabel('Phone number', 'phoneNumber')}
                    value={form.phoneNumber}
                    disabled
                    helperText={lockHint('phoneNumber')}
                    name="phoneNumber"
                  />
                ),
              })}
            </>
          )}
          {field({
            label: 'Gender',
            path: 'gender',
            editable: true,
            control: !locked.has('gender') ? (
              <Select
                label="Gender"
                fullWidth
                variant={hcpProfileFormFieldVariant}
                value={form.gender}
                onChange={change('gender')}
                name="gender"
                error={Boolean(fieldErrors.gender)}
                helperText={fieldErrors.gender || ''}
              >
                <MenuItem value="" disabled>
                  <em>—</em>
                </MenuItem>
                {genderOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Select
                label="Gender"
                fullWidth
                variant={hcpProfileFormFieldVariant}
                value={form.gender}
                disabled
                helperText={lockHint('gender')}
                name="gender"
              >
                <MenuItem value="" disabled>
                  <em>—</em>
                </MenuItem>
                {genderOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            ),
          })}
          {showIdUpload ? (
            <Grid item xs={12}>
              <IdDocumentUploadSection
                existingFile={segmentData.idImage}
                localFile={idFile}
                validationConfig={mergedConfig}
                attachmentRevisions={attachmentRevisions}
                slotKey="general:id_image"
                showPriorVersions={showAttachmentPriorVersions}
                fileFieldId="hcp_profile_general_id_document"
                onFileChange={(f) => {
                  setFieldErrors((prev) => {
                    if (!prev.idImageFile && !prev.idImage) return prev;
                    const next = { ...prev };
                    delete next.idImageFile;
                    delete next.idImage;
                    return next;
                  });
                  setIdFile(f);
                }}
                helperText={idDocumentUploadHelperText}
                profileCardStyle={cardFieldLayout}
                fileError={fieldErrors.idImageFile || fieldErrors.idImage || ''}
              />
            </Grid>
          ) : null}
        </>
      ) : null}
    </>
  );

  const personalFieldsViewInner = cardFieldLayout ? (
    <>
      {field({
        label: 'First name',
        path: 'firstName',
        editable: false,
        hintKey: 'firstName',
      })}
      {field({
        label: 'Last name',
        path: 'lastName',
        editable: false,
        hintKey: 'lastName',
      })}
      {field({
        label: 'Email',
        path: 'email',
        editable: false,
        type: 'email',
        hintKey: 'email',
      })}
      {field({
        label: 'Category',
        path: 'categoryName',
        editable: false,
        hintKey: 'category',
      })}
      {showExtendedPersonal ? (
        <>
          {field({
            label: 'Date of birth',
            path: 'dateOfBirth',
            editable: false,
            type: 'date',
            hintKey: 'dateOfBirth',
          })}
          {field({
            label: 'Nationality',
            path: 'nationality',
            editable: false,
            hintKey: 'nationality',
            formatDisplay: (v) =>
              nationalityOptions.find((o) => o.value === v)?.label ??
              String(v ?? '—'),
          })}
          {field({
            label: 'ID number',
            path: 'idNumber',
            editable: false,
            hintKey: 'idNumber',
          })}
          {field({
            label: 'Country code',
            path: 'countryCode',
            editable: false,
            hintKey: 'countryCode',
            formatDisplay: (v) =>
              countryCodeDialOptions.find((o) => o.value === v)?.label ??
              String(v ?? '—'),
          })}
          {field({
            label: 'Phone number',
            path: 'phoneNumber',
            editable: false,
            hintKey: 'phoneNumber',
          })}
          {field({
            label: 'Gender',
            path: 'gender',
            editable: false,
            hintKey: 'gender',
            formatDisplay: (v) =>
              genderOptions.find((o) => o.value === v)?.label ??
              String(v ?? '—'),
          })}
          {showIdUpload ? (
            <IdDocumentUploadSection
              existingFile={segmentData.idImage}
              attachmentRevisions={attachmentRevisions}
              slotKey="general:id_image"
              showPriorVersions={showAttachmentPriorVersions}
              editable={false}
              profileCardStyle={cardFieldLayout}
            />
          ) : null}
        </>
      ) : null}
    </>
  ) : (
    <>
      <Grid item xs={12} sm={6}>
        <TextField
          {...hcpProfileTextFieldProps}
          label={profileSignupLabel('First name', 'firstName')}
          name="firstName_view"
          disabled
          value={viewSnapshot.firstName}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          {...hcpProfileTextFieldProps}
          label={profileSignupLabel('Last name', 'lastName')}
          name="lastName_view"
          disabled
          value={viewSnapshot.lastName}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          {...hcpProfileTextFieldProps}
          label={profileSignupLabel('Email', 'email')}
          name="email_view"
          type="email"
          disabled
          value={viewSnapshot.email}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          {...hcpProfileTextFieldProps}
          label={profileSignupLabel('Category', 'category')}
          name="categoryName_view"
          disabled
          value={viewSnapshot.categoryName ? viewSnapshot.categoryName : '—'}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DatePicker
          label={profileSignupLabel('Date of birth', 'dateOfBirth')}
          name="dateOfBirth_view"
          fullWidth
          disabled
          value={isoDateToDatePickerValue(viewSnapshot.dateOfBirth)}
          onChange={() => {}}
          TextFieldProps={hcpProfileDatePickerTextFieldProps}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Select
          label="Nationality"
          name="nationality_view"
          fullWidth
          disabled
          variant={hcpProfileFormFieldVariant}
          value={viewSnapshot.nationality}
        >
          <MenuItem value="" disabled>
            <em>Select…</em>
          </MenuItem>
          {nationalityOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          {...hcpProfileTextFieldProps}
          label={profileSignupLabel('ID number', 'idNumber')}
          name="idNumber_view"
          disabled
          value={viewSnapshot.idNumber}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Select
          label="Country Code"
          name="countryCode_view"
          fullWidth
          disabled
          variant={hcpProfileFormFieldVariant}
          value={viewSnapshot.countryCode}
        >
          <MenuItem value="" disabled>
            <em>Select code</em>
          </MenuItem>
          {countryCodeDialOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          {...hcpProfileTextFieldProps}
          label={profileSignupLabel('Phone number', 'phoneNumber')}
          name="phoneNumber_view"
          type="tel"
          disabled
          value={viewSnapshot.phoneNumber}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Select
          label="Gender"
          name="gender_view"
          fullWidth
          disabled
          variant={hcpProfileFormFieldVariant}
          value={viewSnapshot.gender}
        >
          <MenuItem value="" disabled>
            <em>—</em>
          </MenuItem>
          {genderOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      {showIdUpload ? (
        <Grid item xs={12}>
          <IdDocumentUploadSection
            existingFile={segmentData.idImage}
            attachmentRevisions={attachmentRevisions}
            slotKey="general:id_image"
            showPriorVersions={showAttachmentPriorVersions}
            editable={false}
            profileCardStyle={cardFieldLayout}
          />
        </Grid>
      ) : null}
    </>
  );

  const addressFieldsEdit = (
    <>
      <Grid item xs={12} sm={4} key="addressCountry">
        {!locked.has('addressCountry') ? (
          <Select
            label="Country"
            fullWidth
            variant={hcpProfileFormFieldVariant}
            required={mergedConfig.addressCountryRequired}
            value={form.addressCountry}
            onChange={(e) => {
              const v = e.target.value;
              setFieldErrors((prev) => {
                if (
                  !prev.addressCountry &&
                  !prev.addressState &&
                  !prev.addressCity
                )
                  return prev;
                const next = { ...prev };
                delete next.addressCountry;
                delete next.addressState;
                delete next.addressCity;
                return next;
              });
              setForm((p) => ({
                ...p,
                addressCountry: v,
                addressState: '',
                addressCity: '',
              }));
            }}
            name="addressCountry"
            error={Boolean(fieldErrors.addressCountry)}
            helperText={fieldErrors.addressCountry || ''}
          >
            <MenuItem value="" disabled>
              <em>Select country</em>
            </MenuItem>
            {addressCountrySelectOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Select
            label="Country"
            fullWidth
            variant={hcpProfileFormFieldVariant}
            value={form.addressCountry}
            disabled
            helperText={lockHint('addressCountry')}
            name="addressCountry"
          >
            <MenuItem value="" disabled>
              <em>Select country</em>
            </MenuItem>
            {addressCountrySelectOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        )}
      </Grid>
      <Grid item xs={12} sm={4} key="addressState">
        {!locked.has('addressState') ? (
          <Select
            label="State / region"
            fullWidth
            variant={hcpProfileFormFieldVariant}
            required={mergedConfig.addressStateRequired}
            value={form.addressState}
            disabled={!form.addressCountry}
            onChange={(e) => {
              const v = e.target.value;
              setFieldErrors((prev) => {
                if (!prev.addressState && !prev.addressCity) return prev;
                const next = { ...prev };
                delete next.addressState;
                delete next.addressCity;
                return next;
              });
              setForm((p) => ({ ...p, addressState: v, addressCity: '' }));
            }}
            name="addressState"
            error={Boolean(fieldErrors.addressState)}
            helperText={fieldErrors.addressState || ''}
          >
            <MenuItem value="" disabled>
              <em>Select state / region</em>
            </MenuItem>
            {addressStateSelectOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Select
            label="State / region"
            fullWidth
            variant={hcpProfileFormFieldVariant}
            value={form.addressState}
            disabled
            helperText={lockHint('addressState')}
            name="addressState"
          >
            <MenuItem value="" disabled>
              <em>Select state / region</em>
            </MenuItem>
            {addressStateSelectOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        )}
      </Grid>
      <Grid item xs={12} sm={4} key="addressCity">
        {!locked.has('addressCity') ? (
          <Select
            label="City"
            fullWidth
            variant={hcpProfileFormFieldVariant}
            required={mergedConfig.addressCityRequired}
            value={form.addressCity}
            disabled={!form.addressState}
            onChange={(e) => {
              setFieldErrors((prev) => {
                if (!prev.addressCity) return prev;
                const next = { ...prev };
                delete next.addressCity;
                return next;
              });
              setForm((p) => ({ ...p, addressCity: e.target.value }));
            }}
            name="addressCity"
            error={Boolean(fieldErrors.addressCity)}
            helperText={fieldErrors.addressCity || ''}
          >
            <MenuItem value="" disabled>
              <em>Select city</em>
            </MenuItem>
            {addressCitySelectOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Select
            label="City"
            fullWidth
            variant={hcpProfileFormFieldVariant}
            value={form.addressCity}
            disabled
            helperText={lockHint('addressCity')}
            name="addressCity"
          >
            <MenuItem value="" disabled>
              <em>Select city</em>
            </MenuItem>
            {addressCitySelectOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        )}
      </Grid>
      {field({
        label: 'Street',
        path: 'addressStreet',
        editable: true,
        hintKey: 'addressStreet',
      })}
      {field({
        label: 'Zip / postal code',
        path: 'addressZipCode',
        editable: true,
        hintKey: 'addressZipCode',
      })}
    </>
  );

  const addressFieldsViewInner = cardFieldLayout ? (
    <>
      {field({
        label: 'Country',
        path: 'addressCountry',
        editable: false,
        formatDisplay: (v) => labelForAddressCountry(v),
      })}
      {field({
        label: 'State / region',
        path: 'addressState',
        editable: false,
        formatDisplay: (v) =>
          labelForAddressState(
            normalizeAddressCountryValue(segmentData.addressCountry),
            v
          ),
      })}
      {field({
        label: 'City',
        path: 'addressCity',
        editable: false,
        formatDisplay: (v) =>
          labelForAddressCity(
            normalizeAddressCountryValue(segmentData.addressCountry),
            segmentData.addressState,
            v
          ),
      })}
      {field({
        label: 'Street',
        path: 'addressStreet',
        editable: false,
        hintKey: 'addressStreet',
      })}
      {field({
        label: 'Zip / postal code',
        path: 'addressZipCode',
        editable: false,
        hintKey: 'addressZipCode',
      })}
    </>
  ) : (
    <>
      <Grid item xs={12} sm={4}>
        <Select
          label="Country"
          name="addressCountry_view"
          fullWidth
          disabled
          variant={hcpProfileFormFieldVariant}
          value={viewSnapshot.addressCountry}
        >
          <MenuItem value="" disabled>
            <em>Select country</em>
          </MenuItem>
          {viewAddressCountrySelectOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Select
          label="State / region"
          name="addressState_view"
          fullWidth
          disabled
          variant={hcpProfileFormFieldVariant}
          value={viewSnapshot.addressState}
        >
          <MenuItem value="" disabled>
            <em>Select state / region</em>
          </MenuItem>
          {viewAddressStateSelectOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Select
          label="City"
          name="addressCity_view"
          fullWidth
          disabled
          variant={hcpProfileFormFieldVariant}
          value={viewSnapshot.addressCity}
        >
          <MenuItem value="" disabled>
            <em>Select city</em>
          </MenuItem>
          {viewAddressCitySelectOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={12}>
        <TextField
          {...hcpProfileTextFieldProps}
          label={profileSignupLabel('Street', 'addressStreet')}
          name="addressStreet_view"
          disabled
          value={viewSnapshot.addressStreet}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          {...hcpProfileTextFieldProps}
          label={profileSignupLabel('Zip / postal code', 'addressZipCode')}
          name="addressZipCode_view"
          disabled
          value={viewSnapshot.addressZipCode}
        />
      </Grid>
    </>
  );

  return (
    <div
      className={`hcp-general-info-tab w-100${
        cardFieldLayout ? ` ${profileCardStyles.tabContentStack}` : ''
      }`}
    >
      {headerBar}
      {cardFieldLayout ? <div className={profileCardStyles.divider} /> : null}

      {editable ? (
        <Grid container spacing={dualCol ? 3 : 0} className="w-100 m-0">
          {showPersonal ? (
            <Grid item xs={12} lg={dualCol ? 6 : 12}>
              <Grid
                container
                rowSpacing={0}
                columnSpacing={2}
                className={`w-100 ${profileCardStyles.wizardFormGrid}`}
              >
                {personalFieldsEdit}
              </Grid>
            </Grid>
          ) : null}
          {showAddress ? (
            <Grid item xs={12} lg={dualCol ? 6 : 12}>
              <Grid
                container
                rowSpacing={0}
                columnSpacing={2}
                className={`w-100 ${profileCardStyles.wizardFormGrid}`}
              >
                {addressFieldsEdit}
              </Grid>
            </Grid>
          ) : null}
        </Grid>
      ) : dualCol && showPersonal && showAddress ? (
        <Grid container spacing={3} className="w-100 m-0">
          <Grid item xs={12} lg={6}>
            {cardFieldLayout ? (
              personalFieldsViewInner
            ) : (
              <Grid
                container
                rowSpacing={0}
                columnSpacing={2}
                className={`w-100 ${profileCardStyles.wizardFormGrid}`}
              >
                {personalFieldsViewInner}
              </Grid>
            )}
          </Grid>
          <Grid item xs={12} lg={6}>
            {cardFieldLayout ? (
              addressFieldsViewInner
            ) : (
              <Grid
                container
                rowSpacing={0}
                columnSpacing={2}
                className={`w-100 ${profileCardStyles.wizardFormGrid}`}
              >
                {addressFieldsViewInner}
              </Grid>
            )}
          </Grid>
        </Grid>
      ) : (
        <>
          {showPersonal ? (
            cardFieldLayout ? (
              personalFieldsViewInner
            ) : (
              <Grid
                container
                rowSpacing={0}
                columnSpacing={2}
                className={`w-100 ${profileCardStyles.wizardFormGrid}`}
              >
                {personalFieldsViewInner}
              </Grid>
            )
          ) : null}
          {showAddress ? (
            cardFieldLayout ? (
              addressFieldsViewInner
            ) : (
              <Grid
                container
                rowSpacing={0}
                columnSpacing={2}
                className={`w-100 ${profileCardStyles.wizardFormGrid}`}
              >
                {addressFieldsViewInner}
              </Grid>
            )
          ) : null}
        </>
      )}

      {cardActions}
    </div>
  );
}
